import { useEffect, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { CalendarDays, Plus, X } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  NON_ROUTINE_EVENTS,
  type CalendarEventEntry,
  type Equipment,
} from '@/types/equipment'

export function CalendarEventsModal({
  target,
  onClose,
  onSave,
}: {
  target: Equipment | null
  onClose: () => void
  onSave: (events: CalendarEventEntry[]) => void
}) {
  const { copy } = useEquipmentScope()
  const [events, setEvents] = useState<CalendarEventEntry[]>([])
  const [date, setDate] = useState('')
  const [event, setEvent] = useState(NON_ROUTINE_EVENTS[0])
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!target) return
    setEvents(target.calendar?.events ?? [])
    setDate('')
    setEvent(NON_ROUTINE_EVENTS[0])
    setNote('')
  }, [target])

  const addEvent = () => {
    if (!date) return
    setEvents((prev) =>
      [...prev, { date, event, note: note.trim() || undefined }].sort((a, b) =>
        a.date.localeCompare(b.date),
      ),
    )
    setDate('')
    setNote('')
  }

  const removeEvent = (i: number) => setEvents((prev) => prev.filter((_, idx) => idx !== i))

  const handleDone = () => {
    onSave(events)
    onClose()
  }

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        {target && (
          <>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fuchsia-50 text-fuchsia-600'>
                <CalendarDays className='h-5 w-5' />
              </div>
              <div>
                <h2 className='text-base font-semibold text-gray-900'>Calendar-based events</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  Non-routine events scheduled to a specific date, chosen from a predefined list.
                </p>
              </div>
            </div>

            <div className='mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800'>
              Applies to {copy.type}: {target.equipmentType} — all {copy.unitMany}
            </div>

            <p className='mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Scheduled dates
            </p>
            <div className='mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200'>
              {events.length === 0 ? (
                <p className='px-3 py-4 text-center text-xs text-gray-400'>No dates scheduled yet.</p>
              ) : (
                events.map((ev, i) => (
                  <div key={`${ev.date}-${i}`} className='flex items-center gap-3 px-3 py-2 text-sm'>
                    <span className='w-24 shrink-0 font-mono text-xs font-semibold text-blue-700'>
                      {ev.date}
                    </span>
                    <span className='min-w-0 flex-1 truncate text-gray-800'>
                      {ev.event}
                      {ev.note && <span className='ml-2 text-xs text-gray-400'>· {ev.note}</span>}
                    </span>
                    <button
                      type='button'
                      onClick={() => removeEvent(i)}
                      className='shrink-0 text-gray-300 transition-colors hover:text-rose-500'
                      title='Remove'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                ))
              )}
            </div>

            <p className='mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Add event
            </p>
            <div className='mt-2 grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs font-medium text-gray-600'>Date</label>
                <Input
                  type='date'
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className='mt-1'
                />
              </div>
              <div>
                <label className='text-xs font-medium text-gray-600'>Event (predefined)</label>
                <Select value={event} onValueChange={setEvent}>
                  <SelectTrigger className='mt-1 w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NON_ROUTINE_EVENTS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='mt-3'>
              <label className='text-xs font-medium text-gray-600'>Note (optional)</label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder='e.g., vendor PO #, technician…'
                className='mt-1'
              />
            </div>
            <div className='mt-3'>
              <Button variant='outline' size='sm' onClick={addEvent} disabled={!date}>
                <Plus className='mr-1 h-4 w-4' />
                Add to calendar
              </Button>
            </div>

            <DialogFooter className='mt-6'>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleDone}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
